import type { JSX } from 'react';
import { useForm } from 'react-hook-form';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { Experience } from '@/features/experiences/types/experience.types';
import {
  VerificationFormFields,
  type VerificationFormData,
} from '@/features/verifications/components/VerificationFormFields';
import type { CreateVerificationRequest } from '@/features/verifications/types/verification.types';

interface CandidateVerificationFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (request: CreateVerificationRequest) => void;
  experience: Pick<Experience, 'jobTitle' | 'companyName'>;
}

export function CandidateVerificationFormDialog({
  open,
  onOpenChange,
  onCreate,
  experience,
}: CandidateVerificationFormDialogProps): JSX.Element {
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<VerificationFormData>({
    defaultValues: { refereeEmail: '', refereeName: '', refereeJobTitle: '' },
  });

  const handleClose = (): void => onOpenChange(false);

  const onSubmit = handleSubmit((data) => {
    onCreate({
      refereeEmail: data.refereeEmail,
      refereeName: data.refereeName,
      refereeJobTitle: data.refereeJobTitle || null,
    });
    reset();
    handleClose();
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Add reference contact</DialogTitle>
          <DialogDescription>
            {experience.jobTitle} at {experience.companyName}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={onSubmit} className="space-y-4">
          <VerificationFormFields register={register} errors={errors} variant="contact" />
          <p className="text-sm text-muted-foreground">
            Your contact won&apos;t be notified yet. You can edit these details until you send the
            verification request.
          </p>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose}>
              Cancel
            </Button>
            <Button type="submit">Save contact</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
